import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMetaAccountsLists } from "../../../../hooks/useMetaAccountsLists";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import {
  generateSocialDescriptionFromUrl,
  checkAndSaveUserOnboardingChecklist,
} from "../../../../actions/adsCampaigns";
import { startTrackHomeFeatureUsage } from "../../../../actions/homeFeatureActivity";
import { HOME_FEATURE_ID } from "../../../../data/homeFeatures";
import {
  startUploadOrganicContent,
  startUpdateScheduledContent,
} from "../../../../actions/user";
import {
  ALERT_ICON_TYPE_SUCCESS,
  IMAGE_FORMATS,
  INSTAGRAM as INSTAGRAM_CONSTANT,
  META as META_CONSTANT,
  TIKTOK as TIKTOK_CONSTANT,
  VIDEO_FORMATS,
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  YOUTUBE as YOUTUBE_CONSTANT,
  FACEBOOK as FACEBOOK_CONSTANT,
  INSTAGRAM_CONTENT_TYPE_CAROUSEL,
  INSTAGRAM_CONTENT_TYPE_IMAGE,
  FACEBOOK_CONTENT_TYPE_CAROUSEL,
  TIKTOK_CONTENT_TYPE_CAROUSEL,
  MIN_CAROUSEL_IMAGES,
  LINKEDIN as LINKEDIN_CONSTANT,
  LINKEDIN_PUBLISH_AS,
  CHECKLIST_PUBLISH_CONTENT,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import useAgencyIsAppSumo from "../../../../hooks/useAgencyIsAppSumo";
import { getExtensionFile, logoPlatform } from "../../../../utils/string";
import {
  AVAILABLE_PLATFORMS,
  platformRegistry,
  VIDEO_ONLY_PLATFORMS,
  PLATFORMS_WITHOUT_CAROUSEL,
} from "../sharePlatforms";
import {
  ALERT_SHARE_SCHEDULED_SUCCESS,
  ALERT_SHARE_SUCCESS,
  READY,
  CONFIRM_CHANGE_CONTENT_TYPE_TITLE,
  CONFIRM_CHANGE_CONTENT_TYPE_MESSAGE_CAROUSEL,
  CONFIRM_CHANGE_CONTENT_TYPE_MESSAGE_POST,
} from "../../../../i18n/keysTranslations";
import { ConfirmAlert } from "../../../Alerts/Alerts";
import { SimpleAlert } from "../../../Alerts/Alerts";
import { getShareModalSchema } from "../schemas/shareModalSchema";
import {
  createPlatformDefaultValues,
  PLATFORM_FIELD_GROUPS,
} from "../utils/platforms";
import useShareModalEdit from "./useShareModalEdit";
import { getIsBlockedActions } from "../../../../actions/getters";
import {
  getContentTypeCategory,
  mapContentTypeForPlatform,
  CONTENT_TYPE_CATEGORY,
  SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG,
  isGeneralDescriptionRequired,
  getShareModalMaxCarouselImages,
} from "../utils/contentTypeSync";
import { normalizeCarouselItem } from "../../../../utils/carouselUtils";
import { getMediaDimensionsAsync } from "../../../../hooks/useMediaDimensions";
import { isAspectRatio4x5 } from "../../../../utils/dimensions";
import { isOrganicPublishFullySuccessful } from "../../../../utils/organicPublishOutcome";
import { runOrganicPublishRetryFlow } from "../../../../utils/runOrganicPublishRetryFlow";
import { useContentModals } from "../../../../contexts/contentModalsContext";

const mapSelectedItemToCarouselSlide = (selectedItem) => ({
  url: selectedItem.url,
  mediaData: selectedItem.mediaData || {},
});

const mapSelectedItemsToCarouselSlides = (selectedItems) =>
  selectedItems
    .filter((item) => item?.url)
    .map((item) => mapSelectedItemToCarouselSlide(item));

const withCarouselOrder = (items) =>
  items.map((item, index) => ({
    ...item,
    order: index + 1,
  }));

export const useShareModalMultiple = ({
  item,
  modalOpen,
  scheduledDate,
  onCloseModal,
  onRefreshCalendar,
  onScheduledShareSuccess,
  t,
  i18n,
  isEditing = false,
  editingData = null,
}) => {
  const dispatch = useDispatchApp();
  const {
    ADS_CAMPAIGN_GENERATE_SCHEDULED_CONTENT_ACTION,
    ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
  } = useCreditActionCosts();
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const { userID } = dispatch(getPathDatabaseByRole());
  const { openGallery: pushGallery, close: closeContentModal } =
    useContentModals();
  const galleryIdRef = useRef(null);
  const onGalleryItemsSelectedRef = useRef(() => {});

  const closeGallery = useCallback(() => {
    if (!galleryIdRef.current) return;
    closeContentModal(galleryIdRef.current);
    galleryIdRef.current = null;
  }, [closeContentModal]);

  useEffect(() => {
    return () => {
      if (galleryIdRef.current) {
        closeContentModal(galleryIdRef.current);
        galleryIdRef.current = null;
      }
    };
  }, [closeContentModal]);

  const integrations = useSelectorApp((state) => state.user?.integrations);

  const { uniqueInstagramBusinessList, uniqueFacebookPagesList } =
    useMetaAccountsLists();

  const [currentItems, setCurrentItems] = useState(() => {
    if (!item) return [];

    if (
      item?.isCarousel &&
      item?.carouselImages &&
      item.carouselImages.length > 1
    ) {
      return item.carouselImages.map((img, index) => ({
        url: typeof img === "string" ? img : img.url,
        mediaData: typeof img === "object" ? img.mediaData || {} : {},
        order: index + 1,
      }));
    }

    return [item];
  });

  const [isCarouselMode, setIsCarouselMode] = useState(() => {
    return !!(
      item?.isCarousel &&
      item?.carouselImages &&
      item.carouselImages.length > 1
    );
  });
  const [isConfigOpen, setIsConfigOpen] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [isAspectRatioModalOpen, setIsAspectRatioModalOpen] = useState(false);
  const aspectRatioModalResolverRef = useRef(null);
  const previousLanguageRef = useRef(null);
  const hasGeneratedForLanguageRef = useRef(false);
  const hasHydratedFormRef = useRef(false);
  const previousURLRef = useRef(null);
  const isGeneratingDescriptionRef = useRef(false);
  const isInitialCommonStateRef = useRef(true);

  const defaultLanguage = useMemo(() => {
    return (i18n.language || i18n.resolvedLanguage || "en").split("-")[0];
  }, [i18n.language, i18n.resolvedLanguage]);

  const { editingHydratedState } = useShareModalEdit({
    isEditing,
    editingData,
    defaultLanguage,
    scheduledDate,
    uniqueInstagramBusinessList,
    uniqueFacebookPagesList,
    integrations,
  });

  const schema = useMemo(
    () => getShareModalSchema({ selectedPlatforms }),
    [selectedPlatforms]
  );

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      language: defaultLanguage,
      description: "",
      scheduledDate: scheduledDate || null,
      platformsData: createPlatformDefaultValues(),
      platforms: [],
    },
    shouldUnregister: true,
  });

  const {
    watch,
    setValue,
    getValues,
    reset,
    unregister,
    handleSubmit,
    formState: { errors },
  } = methods;

  useEffect(() => {
    setValue("platforms", selectedPlatforms);
  }, [selectedPlatforms, setValue]);

  useEffect(() => {
    if (!item) {
      setCurrentItems([]);
      setIsCarouselMode(false);
      return;
    }

    if (
      item?.isCarousel &&
      item?.carouselImages &&
      item.carouselImages.length > 1
    ) {
      const carouselItems = item.carouselImages.map((img, index) => ({
        url: typeof img === "string" ? img : img.url,
        mediaData: typeof img === "object" ? img.mediaData || {} : {},
        order: index + 1,
      }));
      setCurrentItems(carouselItems);
      setIsCarouselMode(true);
    } else {
      setCurrentItems([item]);
      setIsCarouselMode(false);
    }
  }, [item]);

  useEffect(() => {
    if (!modalOpen) {
      setIsGeneratingDescription(false);
      previousLanguageRef.current = null;
      hasGeneratedForLanguageRef.current = false;
      hasHydratedFormRef.current = false;
      isInitialCommonStateRef.current = true;
      return;
    }

    if (hasHydratedFormRef.current) return;
    if (isEditing && !editingHydratedState) return;

    const defaultFormValues = {
      language: defaultLanguage,
      description: "",
      scheduledDate: scheduledDate || null,
      platformsData: createPlatformDefaultValues(),
      platforms: [],
    };

    const initialState = isEditing
      ? editingHydratedState
      : { formValues: defaultFormValues, selectedPlatforms: [] };

    reset(initialState.formValues);
    setSelectedPlatforms(initialState.selectedPlatforms);
    setIsConfigOpen(true);
    closeGallery();
    previousLanguageRef.current = isEditing
      ? initialState.formValues.language
      : null;
    hasGeneratedForLanguageRef.current = isEditing ? true : false;
    hasHydratedFormRef.current = true;
  }, [
    modalOpen,
    reset,
    defaultLanguage,
    scheduledDate,
    isEditing,
    editingHydratedState,
    closeGallery,
  ]);

  const instagramContentType = watch("platformsData.instagram.contentType");
  const facebookContentType = watch("platformsData.facebook.contentType");
  const tiktokContentType = watch("platformsData.tiktok.contentType");

  useEffect(() => {
    const isCarouselFromContentType =
      instagramContentType === INSTAGRAM_CONTENT_TYPE_CAROUSEL ||
      facebookContentType === FACEBOOK_CONTENT_TYPE_CAROUSEL ||
      tiktokContentType === TIKTOK_CONTENT_TYPE_CAROUSEL;

    if (isCarouselFromContentType || isCarouselMode) {
      setIsCarouselMode(true);
    }
  }, [
    instagramContentType,
    facebookContentType,
    tiktokContentType,
    isCarouselMode,
  ]);

  const currentItem = currentItems[0] || {};
  const platformMediaItem = useMemo(() => {
    if (!isCarouselMode || currentItems.length === 0) {
      return currentItems[0] || {};
    }
    return currentItems[currentCarouselIndex] || currentItems[0] || {};
  }, [isCarouselMode, currentItems, currentCarouselIndex]);

  const format = getExtensionFile(currentItem?.url);
  const isImage = IMAGE_FORMATS.includes(format);
  const isVideo = VIDEO_FORMATS.includes(format);

  const platformOptions = useMemo(() => {
    return AVAILABLE_PLATFORMS.map(({ value }) => {
      const config = platformRegistry[value];
      const baseDisabled =
        config?.isDisabled?.({
          isImage,
          isVideo,
        }) ?? false;

      const carouselDisabled =
        isCarouselMode && PLATFORMS_WITHOUT_CAROUSEL.includes(value);

      const disabled = baseDisabled || carouselDisabled;

      const logoSrc = logoPlatform(
        value === META_CONSTANT ? INSTAGRAM_CONSTANT : value
      );

      return {
        value,
        icon: logoSrc,
        disabled,
        label: config?.labelKey ? t(config.labelKey) : value,
      };
    });
  }, [isImage, isVideo, isCarouselMode, t]);

  const invalidVideoPlatform = selectedPlatforms.find(
    (platform) => VIDEO_ONLY_PLATFORMS.includes(platform) && isImage
  );

  const invalidVideoPlatformLabel = invalidVideoPlatform
    ? t(
        platformRegistry[invalidVideoPlatform]?.labelKey || invalidVideoPlatform
      )
    : null;

  const generalLanguage = watch("language");
  const descriptionValue = watch("description");
  const scheduledDateValue = watch("scheduledDate");

  const allFormValues = watch();

  const maxCarouselImages = useMemo(
    () => getShareModalMaxCarouselImages({ selectedPlatforms }),
    [selectedPlatforms]
  );

  const isDescriptionRequired = isGeneralDescriptionRequired({
    selectedPlatforms,
    platformsData: {
      ...allFormValues?.platformsData,
      instagram: {
        ...allFormValues?.platformsData?.instagram,
        contentType: instagramContentType,
      },
      facebook: {
        ...allFormValues?.platformsData?.facebook,
        contentType: facebookContentType,
      },
      tiktok: {
        ...allFormValues?.platformsData?.tiktok,
        contentType: tiktokContentType,
      },
    },
  });

  const openGallery = useCallback(
    ({ appendMode = false } = {}) => {
      if (galleryIdRef.current) return;

      const remainingSlots = Math.max(
        0,
        maxCarouselImages - currentItems.length
      );

      if (appendMode && isCarouselMode && remainingSlots === 0) {
        return;
      }

      const isMultiSelect = appendMode && isCarouselMode && remainingSlots >= 1;

      galleryIdRef.current = pushGallery({
        multiple: isMultiSelect,
        maxSelections: isMultiSelect ? remainingSlots : 1,
        skipAutoSelectOnUpload: isMultiSelect,
        isOutsideCampaign: true,
        showFooterUseSelectedButton: isMultiSelect,
        acceptedFormats: isCarouselMode
          ? IMAGE_FORMATS.concat(ADS_CAMPAIGN_CAROUSEL_FORMAT)
          : IMAGE_FORMATS.concat(VIDEO_FORMATS).concat(
              ADS_CAMPAIGN_CAROUSEL_FORMAT
            ),
        pathStorage: `socialMedia/${userID}/`,
        modeUpload: "sync",
        onCallbackSelectedItems: (selectedItems) =>
          onGalleryItemsSelectedRef.current(selectedItems),
        closeActionsDisabled: false,
        fromCalendar: Boolean(scheduledDate),
        onCloseModal: () => {
          galleryIdRef.current = null;
        },
      });
    },
    [
      pushGallery,
      scheduledDate,
      userID,
      isCarouselMode,
      currentItems.length,
      maxCarouselImages,
    ]
  );

  const hasMissingPlatformFields = useMemo(() => {
    return selectedPlatforms.some((platform) => {
      const data = allFormValues?.platformsData?.[platform];
      if (!data) return true;

      if (platform === INSTAGRAM_CONSTANT) {
        return !data.account || !data.contentType;
      }

      if (platform === FACEBOOK_CONSTANT) {
        return !data.account || !data.contentType;
      }

      if (platform === TIKTOK_CONSTANT) {
        return !data.account || !data.privacy_level || !data.contentType;
      }

      if (platform === YOUTUBE_CONSTANT) {
        return (
          !data.account || !data.title?.trim() || !data.privacyStatus?.trim()
        );
      }

      if (platform === LINKEDIN_CONSTANT) {
        return !data.account;
      }

      return false;
    });
  }, [allFormValues, selectedPlatforms]);

  const isSubmitDisabled =
    isUploading ||
    (isCarouselMode
      ? currentItems.length < MIN_CAROUSEL_IMAGES
      : !currentItems[0]?.url) ||
    !generalLanguage ||
    (isDescriptionRequired && !descriptionValue?.trim()) ||
    hasMissingPlatformFields ||
    Boolean(invalidVideoPlatform) ||
    !selectedPlatforms.length;

  const onTogglePlatform = (platform) => {
    const option = platformOptions.find((opt) => opt.value === platform);
    if (option?.disabled) return;

    setSelectedPlatforms((prev) => {
      if (prev.includes(platform)) {
        const updated = prev.filter((itemValue) => itemValue !== platform);
        setValue(
          `platformsData.${platform}`,
          createPlatformDefaultValues()[platform]
        );
        PLATFORM_FIELD_GROUPS[platform]?.forEach((fieldName) =>
          unregister(fieldName)
        );
        return updated;
      }

      return [...prev, platform];
    });
  };

  const onHandleKeyToggle = (event, platform, isDisabled) => {
    if (isDisabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onTogglePlatform(platform);
    }
  };

  const getGeneratorPlatform = () => {
    const primary = selectedPlatforms[0] || INSTAGRAM_CONSTANT;
    if (primary === TIKTOK_CONSTANT) return TIKTOK_CONSTANT;
    if (primary === YOUTUBE_CONSTANT) return YOUTUBE_CONSTANT;
    if (primary === INSTAGRAM_CONSTANT) return META_CONSTANT;
    return META_CONSTANT;
  };

  useEffect(() => {
    if (!isImage) return;

    setSelectedPlatforms((prev) => {
      const invalidPlatforms = prev.filter((platform) =>
        VIDEO_ONLY_PLATFORMS.includes(platform)
      );

      if (!invalidPlatforms.length) return prev;

      const defaults = createPlatformDefaultValues();
      invalidPlatforms.forEach((platform) => {
        setValue(`platformsData.${platform}`, defaults[platform]);
        PLATFORM_FIELD_GROUPS[platform]?.forEach((fieldName) =>
          unregister(fieldName)
        );
      });

      return prev.filter(
        (platform) => !VIDEO_ONLY_PLATFORMS.includes(platform)
      );
    });
  }, [isImage, setValue, unregister]);

  useEffect(() => {
    if (!isCarouselMode) return;

    setSelectedPlatforms((prev) => {
      const toRemove = prev.filter((p) =>
        PLATFORMS_WITHOUT_CAROUSEL.includes(p)
      );
      if (!toRemove.length) return prev;

      const defaults = createPlatformDefaultValues();

      toRemove.forEach((platform) => {
        setValue(`platformsData.${platform}`, defaults[platform]);
        PLATFORM_FIELD_GROUPS[platform]?.forEach((fieldName) =>
          unregister(fieldName)
        );
      });

      return prev.filter((p) => !PLATFORMS_WITHOUT_CAROUSEL.includes(p));
    });
  }, [isCarouselMode, setValue, unregister]);

  const onGenerateDescription = useCallback(
    async (clearDescription = false) => {
      if (!generalLanguage || !currentItem?.url) return;
      if (isGeneratingDescriptionRef.current) return;

      isGeneratingDescriptionRef.current = true;
      setIsGeneratingDescription(true);

      const previousDescription = clearDescription
        ? ""
        : getValues("description") || "";
      const currentDescription = clearDescription ? "" : previousDescription;

      if (clearDescription) {
        setValue("description", "");
      }

      try {
        const isBlocked = await dispatch(
          getIsBlockedActions({
            action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
            showUsageConfirmationAlert: true,
          })
        );
        if (isBlocked) {
          return;
        }

        const response = await dispatch(
          generateSocialDescriptionFromUrl({
            url: currentItem.url,
            language: generalLanguage,
            description: currentDescription,
          })
        );
        if (response) {
          setValue("description", response);
        } else if (!clearDescription && previousDescription) {
          setValue("description", previousDescription);
        }
      } catch (error) {
        console.error("Error generating description:", error);
        if (!clearDescription && previousDescription) {
          setValue("description", previousDescription);
        }
      } finally {
        isGeneratingDescriptionRef.current = false;
        setIsGeneratingDescription(false);
      }
    },
    //eslint-disable-next-line
    [dispatch, generalLanguage, currentItem?.url, setValue]
  );

  useEffect(() => {
    if (
      !modalOpen ||
      !generalLanguage ||
      isEditing ||
      !currentItem?.url ||
      isGeneratingDescriptionRef.current ||
      !isAppSumoResolved ||
      isAppSumo ||
      !isDescriptionRequired
    ) {
      return;
    }

    const urlChanged = previousURLRef.current !== currentItem.url;
    const languageChanged = previousLanguageRef.current !== generalLanguage;

    if (urlChanged) {
      onGenerateDescription(true);
      previousURLRef.current = currentItem.url;
      previousLanguageRef.current = generalLanguage;
      hasGeneratedForLanguageRef.current = true;
      return;
    }

    if (!hasGeneratedForLanguageRef.current || languageChanged) {
      onGenerateDescription(false);
      hasGeneratedForLanguageRef.current = true;
      previousLanguageRef.current = generalLanguage;
    }
  }, [
    generalLanguage,
    modalOpen,
    isEditing,
    currentItem?.url,
    onGenerateDescription,
    isAppSumo,
    isAppSumoResolved,
    isDescriptionRequired,
  ]);

  const onChangeLinkedInAccount = useCallback(
    (account) => {
      if (account) {
        setValue("platformsData.linkedIn.account", account);
      }
    },
    [setValue]
  );

  const buildPlatformsDataPayloadFromPlatformList = ({
    platformIds,
    formPlatformsData,
  }) => {
    return platformIds.reduce((acc, platform) => {
      const platformValues = formPlatformsData?.[platform] || {};
      if (platform === INSTAGRAM_CONSTANT) {
        const account = platformValues.account || {};
        const instagramPayload = {
          integrationID: account.integrationID,
          pageID: account.pageID,
          instagramID: account.instagramID,
          mediaType: platformValues.contentType,
        };

        if (
          platformValues.collaborators &&
          platformValues.collaborators.length > 0
        ) {
          instagramPayload.collaborators = platformValues.collaborators;
        }

        acc[platform] = instagramPayload;
      } else if (platform === FACEBOOK_CONSTANT) {
        const account = platformValues.account || {};
        acc[platform] = {
          integrationID: account.integrationID,
          pageID: account.id,
          mediaType: platformValues.contentType,
        };
      } else if (platform === TIKTOK_CONSTANT) {
        const account = platformValues.account || {};
        acc[platform] = {
          integrationID: account.integrationID,
          privacy: platformValues.privacy_level,
          mediaType: platformValues.contentType,
          allow: {
            duet: platformValues.disable_duet || false,
            stitch: platformValues.disable_stitch || false,
            comments: platformValues.disable_comment || false,
            is_aigc: platformValues.is_aigc || false,
            auto_add_music: platformValues.auto_add_music || false,
          },
        };
      } else if (platform === YOUTUBE_CONSTANT) {
        const account = platformValues.account || {};
        acc[platform] = {
          integrationID: account.integrationID || account.id,
          title: platformValues.title,
          privacyStatus: platformValues.privacyStatus,
          notifySubscribers: Boolean(platformValues.notifySubscribers),
          madeForKids: Boolean(platformValues.madeForKids),
        };
      } else if (platform === LINKEDIN_CONSTANT) {
        const account = platformValues.account || {};
        const accountData =
          account && typeof account === "object"
            ? account
            : { id: account, integrationID: null };
        const publishAs =
          accountData.publishAs || LINKEDIN_PUBLISH_AS.ORGANIZATION;
        const linkedInPayload = {
          integrationID: accountData.integrationID || accountData.id,
          publishAs,
          mediaType: isImage ? "IMAGE" : isVideo ? "VIDEO" : null,
        };

        if (publishAs === LINKEDIN_PUBLISH_AS.ORGANIZATION && accountData.id) {
          linkedInPayload.adAccountID = accountData.id;
        }

        acc[platform] = linkedInPayload;
      }
      return acc;
    }, {});
  };

  const buildPlatformsDataPayload = (formPlatformsData) => {
    return buildPlatformsDataPayloadFromPlatformList({
      platformIds: selectedPlatforms,
      formPlatformsData,
    });
  };

  const openAspectRatioModal = useCallback(() => {
    return new Promise((resolve) => {
      aspectRatioModalResolverRef.current = resolve;
      setIsAspectRatioModalOpen(true);
    });
  }, []);

  const resolveAspectRatioModal = useCallback((result) => {
    setIsAspectRatioModalOpen(false);
    if (aspectRatioModalResolverRef.current) {
      aspectRatioModalResolverRef.current(result);
      aspectRatioModalResolverRef.current = null;
    }
  }, []);

  const onAspectRatioGoBack = useCallback(() => {
    resolveAspectRatioModal("back");
  }, [resolveAspectRatioModal]);

  const onAspectRatioContinueWithoutChanges = useCallback(() => {
    resolveAspectRatioModal("continue");
  }, [resolveAspectRatioModal]);

  const onAspectRatioResizeAndContinue = useCallback(() => {
    resolveAspectRatioModal("resize");
  }, [resolveAspectRatioModal]);

  const handleShare = handleSubmit(async (formValues) => {
    if (!selectedPlatforms.length || currentItems.length === 0) return;

    let shouldResizeBeforePublish = false;
    let resizeImagesCount = 0;

    const instagramContentType =
      formValues.platformsData?.instagram?.contentType;
    const isInstagramFeedOrCarousel =
      selectedPlatforms.includes(INSTAGRAM_CONSTANT) &&
      (instagramContentType === INSTAGRAM_CONTENT_TYPE_IMAGE ||
        instagramContentType === INSTAGRAM_CONTENT_TYPE_CAROUSEL);

    if (isInstagramFeedOrCarousel) {
      try {
        const imageItems = currentItems.filter((item) => {
          if (!item?.url) return false;
          const ext = getExtensionFile(item.url);
          return IMAGE_FORMATS.includes(ext);
        });

        if (imageItems.length > 0) {
          const dimensionsPromises = imageItems.map((item) =>
            getMediaDimensionsAsync(item.url)
          );
          const dimensionsList = await Promise.all(dimensionsPromises);

          const hasInvalidAspectRatio = dimensionsList.some(
            (dim) => !isAspectRatio4x5(dim)
          );

          if (hasInvalidAspectRatio) {
            const decision = await openAspectRatioModal();

            if (decision === "back" || !decision) {
              return;
            }

            if (decision === "resize") {
              shouldResizeBeforePublish = true;
              resizeImagesCount = Math.max(imageItems.length, 1);
            }
          }
        }
      } catch {
        // If dimensions fail to load, continue without blocking publish
      }
    }

    const creditCost = shouldResizeBeforePublish
      ? 1 + Math.max(resizeImagesCount, 1)
      : 1;
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_SCHEDULED_CONTENT_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: creditCost,
      })
    );
    if (isBlocked) return;

    setIsUploading(true);
    try {
      const scheduledTimestamp = formValues.scheduledDate
        ? formValues.scheduledDate.getTime()
        : null;

      const urlsPayload = currentItems.map((item) => item.url);

      const editingScheduledFlow = Boolean(
        isEditing && editingData?.id && editingData?.scheduleID
      );

      const resolvedDescription = isGeneralDescriptionRequired({
        selectedPlatforms,
        platformsData: formValues.platformsData,
      })
        ? formValues.description || ""
        : "";

      const payload = {
        urls: urlsPayload,
        language: formValues.language,
        description: resolvedDescription,
        platforms: selectedPlatforms,
        platformsData: buildPlatformsDataPayload(formValues.platformsData),
      };

      if (shouldResizeBeforePublish && payload.platformsData?.instagram) {
        payload.platformsData.instagram.resizeBeforePublish = true;
      }

      if (scheduledTimestamp) {
        payload.scheduledDate = scheduledTimestamp;
      }

      let response;
      if (editingScheduledFlow) {
        response = await dispatch(
          startUpdateScheduledContent({
            id: editingData.id,
            queue: editingData.queue,
            scheduleID: editingData.scheduleID,
            payload,
          })
        );
      } else {
        response = await dispatch(startUploadOrganicContent(payload));
      }

      let resolvedUploadResponse = response;

      const isImmediateOrganicShare =
        !scheduledTimestamp && !editingScheduledFlow;

      if (isImmediateOrganicShare) {
        const retryFlowResult = await runOrganicPublishRetryFlow({
          dispatch,
          initialResponse: response,
          t,
          buildRetryPayload: ({ failedPlatforms }) => ({
            urls: urlsPayload,
            language: formValues.language,
            description: resolvedDescription,
            platforms: failedPlatforms,
            platformsData: buildPlatformsDataPayloadFromPlatformList({
              platformIds: failedPlatforms,
              formPlatformsData: formValues.platformsData,
            }),
            isRetry: true,
          }),
        });
        resolvedUploadResponse = retryFlowResult.response;
      }

      const isShareSuccess =
        editingScheduledFlow || scheduledTimestamp
          ? Boolean(resolvedUploadResponse?.ok)
          : isOrganicPublishFullySuccessful(resolvedUploadResponse);

      if (isShareSuccess) {
        const successText = scheduledTimestamp
          ? t(ALERT_SHARE_SCHEDULED_SUCCESS)
          : t(ALERT_SHARE_SUCCESS);
        SimpleAlert({
          title: t(READY),
          text: successText,
          icon: ALERT_ICON_TYPE_SUCCESS,
        });

        dispatch(
          checkAndSaveUserOnboardingChecklist(CHECKLIST_PUBLISH_CONTENT)
        );
        dispatch(
          startTrackHomeFeatureUsage({ featureID: HOME_FEATURE_ID.SCHEDULE })
        );

        if (scheduledTimestamp && formValues.scheduledDate) {
          onScheduledShareSuccess?.({
            scheduledDate: formValues.scheduledDate,
          });
        }

        if (onRefreshCalendar) {
          onRefreshCalendar();
        }

        closeGallery();
        onCloseModal();
      }
    } catch (error) {
      console.error("Error sharing content:", error);
    } finally {
      setIsUploading(false);
    }
  });

  const toggleConfig = () => setIsConfigOpen((prev) => !prev);

  const onContentTypeChange = useCallback(
    async (platform, newContentType) => {
      const platformConfig = SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG[platform];
      if (!platformConfig) {
        return true;
      }

      // Get the current content types for all platforms
      const currentTypes = {
        [INSTAGRAM_CONSTANT]: watch("platformsData.instagram.contentType"),
        [FACEBOOK_CONSTANT]: watch("platformsData.facebook.contentType"),
        [TIKTOK_CONSTANT]: watch("platformsData.tiktok.contentType"),
      };

      // Identify the category of the new content type and the current one
      const newCategory = getContentTypeCategory(
        newContentType,
        platform,
        SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG
      );
      const currentCategory = getContentTypeCategory(
        currentTypes[platform],
        platform,
        SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG
      );

      // A change is "Global" if it involves Carousel as origin or destination
      const isSelectingCarousel =
        newCategory === CONTENT_TYPE_CATEGORY.CAROUSEL;
      const isLeavingCarousel =
        currentCategory === CONTENT_TYPE_CATEGORY.CAROUSEL;
      const isGlobalChange = isSelectingCarousel || isLeavingCarousel;

      // We only ask for confirmation if the change is Global
      if (isGlobalChange) {
        const message = isSelectingCarousel
          ? t(CONFIRM_CHANGE_CONTENT_TYPE_MESSAGE_CAROUSEL)
          : t(CONFIRM_CHANGE_CONTENT_TYPE_MESSAGE_POST);

        const result = await ConfirmAlert({
          title: t(CONFIRM_CHANGE_CONTENT_TYPE_TITLE),
          text: message,
        });

        if (!result.isConfirmed) {
          return false;
        }
      }

      // Determine which platforms to synchronize
      // If it is a Global change, we synchronize all selected platforms
      // If it is not Global (e.g. Post <-> Story), we only synchronize the current platform
      const platformsToSync = isGlobalChange
        ? selectedPlatforms.filter(
            (p) => SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG[p]
          )
        : [platform];

      // Apply the changes
      platformsToSync.forEach((p) => {
        const config = SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG[p];
        if (!config) return;

        if (p === platform) {
          // The platform that originated the change uses the new value directly
          setValue(config.field, newContentType);
        } else {
          // Other platforms use the equivalent value
          // SPECIAL RULE TIKTOK: If you try to synchronize STORY, TikTok must go to Publication (Post)
          let mappedValue = mapContentTypeForPlatform(
            newContentType,
            platform,
            p,
            SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG
          );

          // If the origin is STORY (or equivalent global), mapContentTypeForPlatform should already handle TikTok
          // but we ensure that if the result is story and it is TikTok, we force Publication.
          if (
            p === TIKTOK_CONSTANT &&
            newCategory === CONTENT_TYPE_CATEGORY.STORY
          ) {
            mappedValue = config.publicationValue;
          }

          setValue(config.field, mappedValue);
        }
      });

      // Update the general carousel mode based on the new state
      if (isSelectingCarousel) {
        setIsCarouselMode(true);
      } else if (isLeavingCarousel) {
        setIsCarouselMode(false);
      }

      return true;
    },
    [selectedPlatforms, setValue, watch, t]
  );

  const onAddCarouselImage = useCallback(() => {
    setCurrentCarouselIndex(currentItems.length);
    openGallery({ appendMode: true });
  }, [currentItems.length, openGallery]);

  const onChangeCurrentItem = useCallback(
    ({ selectedItems }) => {
      if (!selectedItems?.length || !isCarouselMode) return;

      const incomingSlides = mapSelectedItemsToCarouselSlides(selectedItems);
      if (!incomingSlides.length) return;

      const isAppending =
        currentItems.length === 0 ||
        currentCarouselIndex >= currentItems.length;

      if (isAppending) {
        const mergedItems = withCarouselOrder(
          [...currentItems, ...incomingSlides].slice(0, maxCarouselImages)
        );
        setCurrentItems(mergedItems);
        setCurrentCarouselIndex(mergedItems.length - 1);
      } else if (incomingSlides.length === 1) {
        const updatedItems = [...currentItems];
        updatedItems[currentCarouselIndex] = {
          ...incomingSlides[0],
          order: currentCarouselIndex + 1,
        };
        setCurrentItems(withCarouselOrder(updatedItems));
      } else {
        const updatedItems = [...currentItems];
        incomingSlides.forEach((slide, slideIndex) => {
          const targetIndex = currentCarouselIndex + slideIndex;
          if (targetIndex >= maxCarouselImages) return;

          if (targetIndex < updatedItems.length) {
            updatedItems[targetIndex] = {
              ...slide,
              order: targetIndex + 1,
            };
          } else {
            updatedItems.push({
              ...slide,
              order: updatedItems.length + 1,
            });
          }
        });
        setCurrentItems(
          withCarouselOrder(updatedItems.slice(0, maxCarouselImages))
        );
      }

      hasGeneratedForLanguageRef.current = false;
      previousURLRef.current = null;
      previousLanguageRef.current = null;
    },
    [isCarouselMode, currentCarouselIndex, currentItems, maxCarouselImages]
  );

  const resetContentSelectionGenerationState = () => {
    hasGeneratedForLanguageRef.current = false;
    previousURLRef.current = null;
    previousLanguageRef.current = null;
  };

  const onSelectNewContent = (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) return;

    if (isCarouselMode) {
      onChangeCurrentItem({ selectedItems });
      return;
    }

    const normalizedCarouselItem = selectedItems
      .map((item) => normalizeCarouselItem(item))
      .find(Boolean);

    if (normalizedCarouselItem) {
      const formattedItems = withCarouselOrder(
        normalizedCarouselItem.carouselImages.map((img) => ({
          url: img.url || img,
          mediaData: img.mediaData || {},
        }))
      ).slice(0, maxCarouselImages);
      setCurrentItems(formattedItems);
      setIsCarouselMode(true);
      setCurrentCarouselIndex(0);
      resetContentSelectionGenerationState();
      return;
    }

    const individualItems = selectedItems.filter((item) => item?.url);

    if (individualItems.length >= MIN_CAROUSEL_IMAGES) {
      const formattedItems = withCarouselOrder(
        mapSelectedItemsToCarouselSlides(
          individualItems.slice(0, maxCarouselImages)
        )
      );
      setCurrentItems(formattedItems);
      setIsCarouselMode(true);
      setCurrentCarouselIndex(0);
      resetContentSelectionGenerationState();
      return;
    }

    const selectedItem = individualItems[0];
    if (!selectedItem) return;

    setCurrentItems([
      {
        url: selectedItem.url,
        mediaData: selectedItem.mediaData || {},
      },
    ]);
    setIsCarouselMode(false);
    setCurrentCarouselIndex(0);
    resetContentSelectionGenerationState();
  };

  const onReorderCarouselImages = useCallback((reorderedImages) => {
    setCurrentItems(
      reorderedImages.map((img, idx) => ({
        ...img,
        order: idx + 1,
      }))
    );
  }, []);

  const onRemoveCarouselImage = useCallback(
    (index) => {
      setCurrentItems((prev) => {
        const filtered = prev.filter((_, i) => i !== index);
        // Keep carousel mode even if there are 0-1 images
        if (currentCarouselIndex >= filtered.length) {
          setCurrentCarouselIndex(Math.max(0, filtered.length - 1));
        }
        return filtered;
      });
    },
    [currentCarouselIndex]
  );

  const generatorPlatformKey = useMemo(() => {
    const primary = getGeneratorPlatform();
    if (primary === TIKTOK_CONSTANT) return "tiktok";
    if (primary === YOUTUBE_CONSTANT) return "youtube";
    if (primary === INSTAGRAM_CONSTANT) return "instagram";
    return "instagram";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatforms]);

  onGalleryItemsSelectedRef.current = (selectedItems) => {
    if (isCarouselMode) {
      onChangeCurrentItem({ selectedItems });
      return;
    }
    onSelectNewContent(selectedItems);
  };

  return {
    methods,
    state: {
      currentItems,
      currentItem,
      platformMediaItem,
      isCarouselMode,
      isConfigOpen,
      isImage,
      isVideo,
      isUploading,
      isGeneratingDescription,
      generalLanguage,
      generatorPlatformKey,
      invalidVideoPlatformLabel,
      isDescriptionRequired,
      maxCarouselImages,
      scheduledDateValue,
      uniqueInstagramBusinessList,
      uniqueFacebookPagesList,
      userID,
      currentCarouselIndex,
      setCurrentCarouselIndex,
      isAspectRatioModalOpen,
    },
    platformOptions,
    selectedPlatforms,
    handlers: {
      handleShare,
      toggleConfig,
      onTogglePlatform,
      onHandleKeyToggle,
      onGenerateDescription,
      openGallery,
      closeGallery,
      onSelectNewContent,
      onChangeLinkedInAccount,
      onReorderCarouselImages,
      onRemoveCarouselImage,
      onContentTypeChange,
      onAddCarouselImage,
      onChangeCurrentItem,
      onAspectRatioGoBack: onAspectRatioGoBack,
      onAspectRatioContinueWithoutChanges: onAspectRatioContinueWithoutChanges,
      onAspectRatioResizeAndContinue: onAspectRatioResizeAndContinue,
    },
    formMeta: {
      errors,
      isSubmitDisabled,
    },
  };
};
